import "@src/pages/businessAnalysis/businessAnalysis.css";
import "../compliance/compliance.css";
import "../appraisals/appraisals.css";
import "./surveys.css";
import { useState } from "react";
import { Drawer, Dropdown, Input, Modal, Table, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getSurveyResponseQuery, getSurveyResultsQuery } from "@src/util/query/survey.query";
import { addSurveyActionMutation, closeSurveyMutation, exportSurveyResultsMutation, getSurveyResponseFormMutation, remindSurveyMutation, removeSurveyActionMutation } from "@src/util/mutation/survey.mutation";
import { answerText as formatAnswer, describeRepeat, formatDate, SURVEY_ANSWERED_BY } from "../appraisals/appraisalHelpers";
import { OurSideModal } from "./ourSide.modal";
import { FormOnPdf } from "../formOnPdf/formOnPdf";

const TYPE_LABEL = { employee: "Employee", customer: "Customer", supplier: "Supplier" };

const QuestionSummary = ({ q, scale }) => {
    const max = scale?.max || 5;
    const total = Math.max(1, (q.distribution || []).reduce((n, c) => n + c, 0));
    const all = (q.yes || 0) + (q.no || 0);
    return (
        <div className="svy-result">
            <div className="svy-result-text">{q.text}</div>
            {q.type === "rating" && (
                <>
                    <div className="cmp-muted" style={{ marginBottom: 6 }}>Average <b style={{ color: "#17222c" }}>{q.average ?? "-"}</b> / {max} · {q.answered} {q.answered === 1 ? "answer" : "answers"}</div>
                    {(q.distribution || []).map((count, i) => (
                        <div className="svy-bar-row" key={i}>
                            <span>{i + 1} {scale?.labels?.[i] || ""}</span>
                            <span className="svy-bar"><span className="svy-bar-fill" style={{ display: "block", width: `${(count / total) * 100}%` }} /></span>
                            <span>{count}</span>
                        </div>
                    ))}
                </>
            )}
            {q.type === "yesno" && (
                <div>Yes <b>{q.yes || 0}</b> ({all ? Math.round(((q.yes || 0) / all) * 100) : 0}%) · No <b>{q.no || 0}</b> ({all ? Math.round(((q.no || 0) / all) * 100) : 0}%)</div>
            )}
            <NewTypeSummary q={q} />
            {["comment", "text", "date"].includes(q.type) && <div className="cmp-muted">{q.answered} {q.answered === 1 ? "answer" : "answers"}</div>}
            {(q.comments || []).map((c, i) => (
                <div className="svy-comment" key={i}>"{c.text}"{c.by && <span className="svy-comment-by">- {c.by}</span>}</div>
            ))}
        </div>
    );
};

// checkboxes, numbers and choices (on both kinds of survey)
const NewTypeSummary = ({ q }) => {
    if (q.type === "checkbox") return <div>Checked <b>{q.yes || 0}</b> of {q.answered}</div>;
    if (q.type === "number") return <div>Average <b>{q.average ?? "-"}</b>{q.min != null && <span className="cmp-muted"> (lowest {q.min}, highest {q.max})</span>} · {q.answered} {q.answered === 1 ? "answer" : "answers"}</div>;
    if (q.type === "choice") {
        const total = Math.max(1, (q.counts || []).reduce((n, c) => n + c.count, 0));
        return (q.counts || []).map((c) => (
            <div className="svy-bar-row" key={c.option}>
                <span>{c.option}</span>
                <span className="svy-bar"><span className="svy-bar-fill" style={{ display: "block", width: `${(c.count / total) * 100}%` }} /></span>
                <span>{c.count}</span>
            </div>
        ));
    }
    return null;
};

// one side's answers to a question on a two-way survey
const SideSummary = ({ label, s, q, scale }) => {
    const max = scale?.max || 5;
    const all = (s.yes || 0) + (s.no || 0);
    return (
        <div className="svy-side">
            <div className="svy-side-label">{label} <span className="cmp-muted">· {s.answered} {s.answered === 1 ? "answer" : "answers"}</span></div>
            {q.type === "rating" && (
                <div className="svy-bar-row" style={{ gridTemplateColumns: "1fr 64px" }}>
                    <span className="svy-bar"><span className="svy-bar-fill" style={{ display: "block", width: `${((s.average || 0) / max) * 100}%` }} /></span>
                    <span><b>{s.average ?? "-"}</b> / {max}</span>
                </div>
            )}
            {q.type === "yesno" && <div>Yes <b>{s.yes || 0}</b> · No <b>{s.no || 0}</b>{all ? <span className="cmp-muted"> ({Math.round(((s.yes || 0) / all) * 100)}% yes)</span> : null}</div>}
            <NewTypeSummary q={{ ...s, type: q.type }} />
            {(s.comments || []).map((c, i) => (
                <div className="svy-comment" key={i}>"{c.text}"{(c.by || c.about) && <span className="svy-comment-by">- {[c.by, c.about && `about ${c.about}`].filter(Boolean).join(" ")}</span>}</div>
            ))}
        </div>
    );
};

// a two-way survey's question: their side and ours next to each other, and how far apart
const TwoWaySummary = ({ q, scale }) => (
    <div className="svy-result">
        <div className="svy-result-text">{q.text} <span className="cmp-muted" style={{ fontWeight: 400, fontSize: 12 }}>· {q.answeredBy === "both" ? "both sides" : q.answeredBy === "us" ? "our side" : "their side"}</span></div>
        <div className={`svy-sides${q.askedThem !== false && q.us ? "" : " one"}`}>
            {q.askedThem !== false && <SideSummary label="Them" s={q} q={q} scale={scale} />}
            {q.us && <SideSummary label="Us" s={q.us} q={q} scale={scale} />}
        </div>
        {q.gap !== undefined && q.gap !== null && (
            <div className={`svy-gap${Math.abs(q.gap) >= 1 ? " wide" : ""}`}>
                Gap {q.gap > 0 ? "+" : ""}{q.gap} - {q.gap > 0 ? "we rate it higher than they do" : q.gap < 0 ? "they rate it higher than we do" : "we agree"}
            </div>
        )}
    </div>
);

/** One survey's results: summaries per question, who has answered, and the actions on it. */
export const SurveyResultsDrawer = ({ visible, surveyId, onClose, onChanged = () => {} }) => {
    const dispatch = useDispatch();
    const notify = (message, error = false) => dispatch(setNotification({ error, notificationState: true, message }));
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);
    const canModify = authenticatedUserDetail?.permission?.Surveys?.modify !== false;
    const { data, loading, refetch } = useQuery(getSurveyResultsQuery, { variables: { id: surveyId }, skip: !visible || !surveyId, fetchPolicy: "network-only" });
    const results = data?.getSurveyResults?.response;
    const [remind, { loading: reminding }] = useMutation(remindSurveyMutation);
    const [close] = useMutation(closeSurveyMutation);
    const [exportResults, { loading: exporting }] = useMutation(exportSurveyResultsMutation);
    const [fetchResponse, { data: responseData, loading: responseLoading }] = useLazyQuery(getSurveyResponseQuery, { fetchPolicy: "network-only" });
    const [addAction, { loading: adding }] = useMutation(addSurveyActionMutation);
    const [removeAction] = useMutation(removeSurveyActionMutation);
    const [viewing, setViewing] = useState(false);
    const [answeringOurs, setAnsweringOurs] = useState(null);
    const [actionText, setActionText] = useState("");
    const pending = (results?.respondents || []).filter((r) => r.status !== "submitted");

    const doRemind = () => Modal.confirm({
        title: `Remind ${pending.length} ${pending.length === 1 ? "person" : "people"}?`,
        content: "Everyone who hasn't answered gets a reminder by email (employees in the app too).",
        okText: "Send reminders",
        onOk: async () => {
            try {
                const res = await remind({ variables: { id: surveyId } });
                notify(res?.data?.remindSurvey?.message || "Reminders sent");
                refetch();
            } catch (err) { notify(err.message, true); }
        },
    });
    const doClose = () => Modal.confirm({
        title: "Close this survey?",
        content: "Nobody else can answer it. The results stay here.",
        okText: "Close survey",
        okButtonProps: { danger: true },
        onOk: async () => {
            try {
                await close({ variables: { id: surveyId } });
                notify("Survey closed");
                refetch();
                onChanged();
            } catch (err) { notify(err.message, true); }
        },
    });
    // same as the compliance audit pack: the file downloads (no blank tab left behind)
    const doExport = async (format) => {
        try {
            const res = await exportResults({ variables: { id: surveyId, format } });
            const out = res?.data?.exportSurveyResults?.response;
            if (out?.url) window.open(out.url, "_blank");
            notify(`${out?.fileName || "Results"} is downloading`);
        } catch (err) {
            notify(err.message, true);
        }
    };
    const copyLink = async (link) => {
        try {
            await navigator.clipboard.writeText(link);
            notify("Link copied - send it however suits them");
        } catch {
            Modal.info({ title: "Their survey link", content: <div style={{ wordBreak: "break-all" }}>{link}</div> });
        }
    };
    const viewAnswers = (row) => { setViewing(true); fetchResponse({ variables: { responseId: row._id } }); };
    const one = responseData?.getSurveyResponse?.response;
    const saveAction = async () => {
        try {
            await addAction({ variables: { id: surveyId, text: actionText } });
            setActionText("");
            refetch();
        } catch (err) { notify(err.message, true); }
    };
    const deleteAction = (action) => Modal.confirm({
        title: "Remove this note?",
        okText: "Remove",
        okButtonProps: { danger: true },
        onOk: async () => {
            try { await removeAction({ variables: { id: surveyId, actionId: action.id } }); refetch(); } catch (err) { notify(err.message, true); }
        },
    });
    const answerText = (q, a, scale) => (!a ? "Not answered" : formatAnswer(q, a, scale) || "-");
    const [fetchForm, { loading: formLoading }] = useMutation(getSurveyResponseFormMutation);
    const downloadForm = async () => {
        try {
            const res = await fetchForm({ variables: { responseId: one._id } });
            const out = res?.data?.getSurveyResponseForm?.response;
            if (out?.url) window.open(out.url, "_blank");
            notify(`${out?.fileName || "The form"} is downloading`);
        } catch (err) { notify(err.message, true); }
    };

    const people = [
        { title: "Name", dataIndex: "name", render: (v) => <span className="cmp-person-name">{v}</span> },
        { title: "", dataIndex: "type", render: (v) => <span className="cmp-muted">{TYPE_LABEL[v]}</span> },
        { title: "Email", dataIndex: "email", render: (v) => v || <Tag>No email</Tag> },
        {
            title: "Status", dataIndex: "status",
            render: (v, row) => (v === "submitted" ? <Tag color="green">Answered{row.submittedAt ? ` ${formatDate(row.submittedAt)}` : ""}</Tag> : <Tag color="gold">Not answered{row.remindersSent ? ` · reminded ${row.remindersSent}x` : ""}</Tag>),
        },
        {
            title: "", dataIndex: "actions", align: "right",
            render: (_, row) => row.status === "submitted"
                ? (!results?.anonymous && <button className="sm-btn" onClick={() => viewAnswers(row)}>View answers</button>)
                : (row.link && <button className="sm-btn" onClick={() => copyLink(row.link)}>Copy link</button>),
        },
    ];
    // two-way: our side for each of them - done by whom, or who it's waiting on
    if (results?.twoWay) {
        people.splice(4, 0, {
            title: "Our side", dataIndex: "ourStatus",
            render: (v, row) => (v === "submitted"
                ? <Tag color="green">Done{row.ourBy ? ` - ${row.ourBy}` : ""}</Tag>
                : results.status === "open" && canModify
                    ? <span><button className="sm-btn" onClick={() => setAnsweringOurs(row._id)}>Answer our side</button>{row.ourWaitingOn?.length ? <span className="cmp-muted" style={{ display: "block", fontSize: 12 }}>Waiting on {row.ourWaitingOn.slice(0, 2).join(", ")}{row.ourWaitingOn.length > 2 ? ` +${row.ourWaitingOn.length - 2}` : ""}</span> : null}</span>
                    : <Tag color="gold">Not yet{row.ourWaitingOn?.length ? ` - ${row.ourWaitingOn[0]}` : ""}</Tag>),
        });
    }

    return (
        <Drawer
            title={results?.title || "Survey"}
            placement="right"
            open={visible}
            onClose={onClose}
            width={results?.twoWay ? 1080 : 880}
            rootClassName="apr-form"
            closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" />}
        >
            {loading || !results ? (
                <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}><Spinner /></div>
            ) : (
                <>
                    <div className="apr-meta">
                        <span><Tag color={results.status === "open" ? "blue" : "default"}>{results.status === "open" ? "Open" : "Closed"}</Tag>{results.anonymous && <Tag color="purple">Anonymous</Tag>}{results.twoWay && <Tag color="purple">Two-way</Tag>}</span>
                        <span>Sent to <b>{results.audience?.label}</b></span>
                        <span>Open <b>{formatDate(results.opensOn)}</b> to <b>{formatDate(results.closesOn)}</b></span>
                        {results.schedule && <span>↻ <b>{describeRepeat(results.schedule.everyMonths)}</b>{results.schedule.active ? ` - next goes out ${formatDate(results.schedule.nextOpenOn)}` : " (stopped)"}</span>}
                        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                            {canModify && results.status === "open" && pending.length > 0 && <button className="sm-btn" disabled={reminding} onClick={doRemind}>Remind ({pending.length})</button>}
                            {canModify && results.status === "open" && <button className="sm-btn" onClick={doClose}>Close survey</button>}
                            <Dropdown trigger={["click"]} menu={{ items: [{ key: "pdf", label: "Results (PDF)" }, { key: "csv", label: "Answers (CSV)", disabled: results.hidden }], onClick: ({ key }) => doExport(key) }}>
                                <button className="sm-btn" disabled={exporting}>{exporting ? "Preparing..." : "Export ▾"}</button>
                            </Dropdown>
                        </span>
                    </div>

                    <div className="ba-stat-strip" style={{ marginBottom: 16 }}>
                        <div className="ba-stat"><span className="ba-stat-num">{results.invited}</span><span className="ba-stat-lbl">Sent to</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{results.responded}</span><span className="ba-stat-lbl">Answered</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{results.responseRate}%</span><span className="ba-stat-lbl">Response rate</span></div>
                        {results.twoWay && <div className="ba-stat"><span className="ba-stat-num">{results.ourDone}/{results.invited}</span><span className="ba-stat-lbl">Our side done</span></div>}
                    </div>

                    {results.hidden ? (
                        <div className="ba-callout info">
                            <div><div className="ba-callout-title">Results appear once {results.minimumForResults} people have answered</div>
                                <div>This survey is anonymous - with fewer answers, it could be possible to tell who said what.</div></div>
                        </div>
                    ) : results.sections.map((section, i) => (
                        <div className="apr-section" key={i}>
                            <div className="apr-section-title">{section.title}</div>
                            {section.questions.map((q) => (results.twoWay ? <TwoWaySummary key={q.id} q={q} scale={results.ratingScale} /> : <QuestionSummary key={q.id} q={q} scale={results.ratingScale} />))}
                        </div>
                    ))}

                    {results.rounds?.length > 1 && (
                        <>
                            <div className="cmp-panel-title" style={{ marginTop: 18 }}>Every round</div>
                            <Table
                                className="curvedTable"
                                size="small"
                                pagination={false}
                                columns={[
                                    { title: "Went out", dataIndex: "opensOn", render: (v, row) => <span style={{ fontWeight: row.current ? 600 : 400 }}>{formatDate(v)}{row.current && <Tag style={{ marginLeft: 6 }}>This one</Tag>}</span> },
                                    { title: "Answered", dataIndex: "responded", render: (v, row) => `${v} of ${row.invited}` },
                                    { title: "Response rate", dataIndex: "responseRate", render: (v) => `${v}%` },
                                    { title: results.twoWay ? "Average (them)" : "Average rating", dataIndex: "average", render: (v) => (v ?? "-") },
                                    results.twoWay && { title: "Average (us)", dataIndex: "ourAverage", render: (v) => (v ?? "-") },
                                ].filter(Boolean)}
                                dataSource={results.rounds.map((r) => ({ ...r, key: r._id }))}
                            />
                        </>
                    )}

                    <div className="cmp-panel-title" style={{ marginTop: 18 }}>What we did</div>
                    <div className="cmp-hint" style={{ marginBottom: 8 }}>How you responded to these results - kept with the survey and in the PDF, as evidence of action taken.</div>
                    {(results.actions || []).map((a) => (
                        <div className="svy-action" key={a.id}>
                            <div className="svy-action-text">{a.text}</div>
                            <div className="svy-action-meta">
                                {a.byName}{a.byName ? ", " : ""}{formatDate(a.at)}
                                {canModify && <button className="cmp-link" style={{ marginLeft: 10 }} onClick={() => deleteAction(a)}>Remove</button>}
                            </div>
                        </div>
                    ))}
                    {!(results.actions || []).length && !canModify && <div className="cmp-muted">Nothing recorded yet.</div>}
                    {canModify && (
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 6 }}>
                            <Input.TextArea className="generic-input-control" autoSize={{ minRows: 1, maxRows: 4 }} maxLength={2000} value={actionText} onChange={(e) => setActionText(e.target.value)}
                                placeholder="e.g. Rota changes agreed with the site manager; welfare hut fixed" />
                            <button className={adding || !actionText.trim() ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={adding || !actionText.trim()} onClick={saveAction}>Add</button>
                        </div>
                    )}

                    <div className="cmp-panel-title" style={{ marginTop: 18 }}>{results.anonymous ? "Who has answered (not what they said)" : "People"}</div>
                    <Table
                        className="curvedTable"
                        columns={people}
                        dataSource={results.respondents.map((r) => ({ ...r, key: r._id }))}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                    />
                </>
            )}

            <Modal open={viewing} width={one?.onForm ? 980 : 640} closable={false} onCancel={() => setViewing(false)} footer={
                <div className="drawer-footer" style={{ display: "flex", gap: 8 }}>
                    {one?.onForm && one.status === "submitted" && <button className="drawer-filled-btn" disabled={formLoading} onClick={downloadForm}>{formLoading ? "Preparing..." : "Download PDF"}</button>}
                    <button className="drawer-outlined-btn" onClick={() => setViewing(false)}>Close</button>
                </div>
            }>
                <div className="modal-header-title">
                    <span style={{ letterSpacing: "0.2px" }}>{one ? `${one.name}'s answers` : "Answers"}</span>
                    <span onClick={() => setViewing(false)}><FontAwesomeIcon className="close" icon={faClose} /></span>
                </div>
                <div className="modal-body" style={{ margin: "16px 0 0" }}>
                    {responseLoading || !one ? <Spinner /> : one.onForm ? (
                        <FormOnPdf pdfUrl={one.formPdfUrl} template={one.template} kind="survey" mode="view"
                            shown={{ respondent: one.answers || {}, us: one.ourAnswers || {} }} stampValues={one.stampValues || {}} />
                    ) : (one.template.sections || []).map((section) => (
                        <div className="apr-section" key={section.id}>
                            <div className="apr-section-title">{section.title}</div>
                            {section.questions.map((q) => {
                                const scale = one.template.ratingScale;
                                if (!one.twoWay) return <div className="apr-question" key={q.id}><div className="apr-question-text">{q.text}</div><div>{answerText(q, one.answers?.[q.id], scale)}</div></div>;
                                // two-way: their answer and ours side by side
                                return (
                                    <div className="apr-question" key={q.id}>
                                        <div className="apr-question-text">{q.text} <span className="cmp-muted" style={{ fontWeight: 400, fontSize: 12 }}>· {SURVEY_ANSWERED_BY[q.answeredBy] || "Them"}</span></div>
                                        <div className="svy-sides">
                                            <div className="svy-side"><div className="svy-side-label">Them</div>{q.answeredBy === "us" ? <span className="cmp-muted">-</span> : answerText(q, one.answers?.[q.id], scale)}</div>
                                            <div className="svy-side"><div className="svy-side-label">Us{one.ourBy ? ` (${one.ourBy})` : ""}</div>{q.answeredBy === "respondent" ? <span className="cmp-muted">-</span> : one.ourStatus === "submitted" ? answerText(q, one.ourAnswers?.[q.id], scale) : <span className="cmp-muted">Not answered yet</span>}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </Modal>
            <OurSideModal responseId={answeringOurs} onClose={() => setAnsweringOurs(null)} onAnswered={() => { refetch(); onChanged(); }} />
        </Drawer>
    );
};
